import { RootState } from 'store';

import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  checkIsLoading,
  checkIsLoadingDone
} from 'common/utils/checkLoading';
import binarySearch from 'common/utils/binarySearch';

import { Station } from '../../types';

import { STATIONS_STATE } from '../../constants';
import { EMPTY_ARRAY } from 'shared/constants';

const useStationsLoadingStatus = () => {
  const stationsLoadingStatus = useSelector((state: RootState) => state[STATIONS_STATE].stations.loadingStatus);
  return stationsLoadingStatus;
};

export const useIsStationsLoading = () => {
  const stationsLoadingStatus = useStationsLoadingStatus();
  return checkIsLoading(stationsLoadingStatus);
};

export const useIsStationsDone = () => {
  const stationsLoadingStatus = useStationsLoadingStatus();
  return checkIsLoadingDone(stationsLoadingStatus);
};

export const useStations = () => {
  const stations = useSelector((state: RootState) => state[STATIONS_STATE].stations.data);
  return stations;
};

export const useStationList = () => {
  const stations = useStations() || EMPTY_ARRAY;
  return useMemo(() => (
    stations.map(({ code }) => ({
      key: code,
      value: code
    }))
  ), [stations]);
};

export const useGetStationsByBrandCode = () => {
  const stations = useStations() || EMPTY_ARRAY;

  const getStationsByBrandCode = useCallback((brandCode: string) => (
    stations.filter(station => station.brandCode === brandCode)
  ), [stations]);

  return getStationsByBrandCode;
};

export const useGetStationByCode = () => {
  const stations = useStations();

  const getStationByCode = useCallback(
    (stationCode?: Station['code']) => stationCode ? binarySearch({ key: 'code', value: stationCode }, stations) : undefined,
    [stations]
  );

  return getStationByCode;
};

export const useStationByCode = (stationCode?: Station['code']) => {
  const getStationByCode = useGetStationByCode();

  return useMemo(() => getStationByCode(stationCode), [getStationByCode, stationCode]);
};

export const useStationByCodes = (stationCodes: Station['code'][]) => {
  const getStationByCode = useGetStationByCode();

  return useMemo(() => stationCodes.reduce((acc, stationCode) => {
    const station = getStationByCode(stationCode);
    
    if (station) {
      acc.push(station);
    }

    return acc;
  }, [] as Station[]), [getStationByCode, stationCodes]);
};

export const useGetStationByCodes = () => {
  const getStationByCode = useGetStationByCode();

  const getStationByCodes = useCallback((stationCodes: Station['code'][]) => (
    stationCodes.reduce((acc, stationCode) => {
      const station = getStationByCode(stationCode);
      if (station) {
        acc.push(station);
      }
      return acc;
    }, [] as Station[])
  ), [getStationByCode]);
  
  return getStationByCodes;
};

export const useFilterExistStationCodes = () => {
  const getStationByCode = useGetStationByCode();

  const filterExistStationCodes = useCallback((stationCodes: Station['code'][] = []) => (
    stationCodes.filter(stationCode => getStationByCode(stationCode))
  ), [getStationByCode]);

  return filterExistStationCodes;
};
